// Top-level app component.
//
// Boots the WASM sim via useSimWorld, renders the hex map + cities,
// mounts the doctrine selection panel, surfaces tile + city info on click,
// and forwards interaction state into the test bridge for E2E.

import type { MatchSetup, SceneNode } from '@aod/protocol';
import type { CitySuggestion } from '@aod/sim-adapter';
import { Minimap, type MinimapPoint, PlayerHud } from '@aod/ui';
import { NAVAL_KINDS } from '@aod/ui/prefabs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isCameraOverCity, sampleBiomeAtWorld } from './audio/biomeLookup.js';
import { getMatchCriticalSoundIds, preloadCriticalSfx } from './audio/preload.js';
import { useAudioDirector, useSounds } from './audio/useSounds.js';
import { ChampionProvider } from './champion/ChampionContext.js';
import { tryChampionCast } from './champion/castAbility.js';
import { useChampionMarkers } from './champion/useChampionMarkers.js';
import { CinematicOverlay } from './cinematics/CinematicOverlay.js';
import { useCinematicDirector } from './cinematics/useCinematicDirector.js';
import { CoachmarkHost } from './coachmark/CoachmarkHost.js';
import { useCoachmarks } from './coachmark/useCoachmarks.js';
import { useMatchCoachmarkSequence } from './coachmark/useMatchCoachmarkSequence.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { FooterHint } from './components/FooterHint.js';
import { HelpModal } from './components/HelpModal.js';
import { MenuScreen } from './components/MenuScreen.js';
import { type MobileHudPanel, MobileHudToggles } from './components/MobileHudToggles.js';
import { Splash } from './components/Splash.js';
import { TopBar } from './components/TopBar.js';
import { useDiplomacyViews } from './dynasty/useDiplomacyViews.js';
import { useTreatyNews } from './dynasty/useTreatyNews.js';
import { useEspionageView } from './espionage/useEspionageView.js';
import { useHeroCaptivityView } from './heroes/useHeroCaptivityView.js';
import { useHeroQuestsView } from './heroes/useHeroQuestsView.js';
import { useT } from './i18n/index.js';
import { isLadderId, markAnnounced, markMastered, newRulePlan } from './lib/graduation.js';
import { readPersistentFlag, writePersistentFlag } from './lib/persistentFlag.js';
import { LobbyScreen } from './multiplayer/lobby/LobbyScreen.js';
import { type GameMode, shouldConnectMpLobby } from './multiplayer/lobby/shouldConnectMpLobby.js';
import { useMpLobby } from './multiplayer/lobby/useMpLobby.js';
import { MultiplayerMatchView } from './multiplayer/match/MultiplayerMatchView.js';
import type { MatchInfo } from './multiplayer/types.js';
import { PauseCard } from './pause/PauseCard.js';
import type { PauseOnDanger } from './pause/detectDangers.js';
import { usePauseController } from './pause/usePauseController.js';
import { axialToWorld } from './render/HexGrid.js';
import { Scene } from './render/Scene.js';
import { scalePoint } from './render/boardScale.js';
import { beginCameraGrab, cancelCameraGrab, dragCameraTo } from './render/camera/cameraInput.js';
import { useCameraView } from './render/camera/cameraView.js';
import type { CameraFocusRequest, Vec3 } from './render/camera/flight.js';
import { getCombatFights, getCombatRounds } from './render/combatRounds.js';
import { readRenderStats } from './render/renderStats.js';
import { summarizeCombatDrawer } from './render/summarizeCombatDrawer.js';
import { useCombatEngagements } from './render/useCombatEngagementsHook.js';
import { type AutosaveEntry, latestAutosave } from './save/autosaveStore.js';
import { useAutosave } from './save/useAutosave.js';
import { SettingsModal } from './settings/SettingsModal.js';
import { useSettings } from './settings/useSettings.js';
import { SetupScreen } from './setup/SetupScreen.js';
import { applyAssistsAtMatchStart, clampSpeed, hintPolicy } from './setup/clientAssists.js';
import { loadLastSetup, saveLastSetup } from './setup/lastSetup.js';
import { moduleLabel } from './setup/presentation.js';
import {
  type SetupDraft,
  draftFromPreset,
  humanAssistsOf,
  toMatchSetup,
} from './setup/setupModel.js';
import { useAttackToasts } from './sim/useAttackToasts.js';
import { usePendingMerchantOffer } from './sim/useMerchantOffer.js';
import { useResearchToasts } from './sim/useResearchToasts.js';
import { useSimWorld } from './sim/useSimWorld/index.js';
import { suggestionAction, useCitySuggestions } from './sim/useSimWorld/suggestions.js';
import { SpectatorView } from './spectate/SpectatorView.js';
import { watchFromSearch } from './spectate/watchLink.js';
import { type Selection, useTestBridge } from './testBridge/useTestBridge.js';
import { useApplyTheme } from './theme/useApplyTheme.js';
import { AttackToastStack } from './ui/AttackToastStack.js';
import { CombatDrawerTicker, CombatModal } from './ui/CombatOverlay/index.js';
import { DiplomacyToasts } from './ui/DiplomacyToasts.js';
import { DoctrinePanel } from './ui/DoctrinePanel/index.js';
import { EventLog } from './ui/EventLog.js';
import { MerchantOfferToast } from './ui/MerchantOfferToast.js';
import { PostVictorySummary } from './ui/PostVictorySummary.js';
import { ResearchToastStack } from './ui/ResearchToastStack.js';
import { RightPanel } from './ui/RightPanel/index.js';
import { visibleInLog } from './ui/RightPanel/milestoneEventText.js';
import { TileTooltip } from './ui/TileTooltip.js';
import { UnitSelectionTray } from './ui/UnitSelectionTray.js';
import { UnitTooltip } from './ui/UnitTooltip.js';
import { VictoryBanner } from './ui/VictoryBanner.js';
import { VictoryProgressHUD } from './ui/VictoryProgressHUD.js';
import { DockedDebugHud } from './ui/layout/DockedDebugHud.js';
import { PanelResizeHandle, usePanelDockStyle } from './ui/layout/PanelDockControls.js';
import { usePanelLayoutSession } from './ui/layout/usePanelLayoutSession.js';
import {
  type PingTarget,
  cityPositions,
  eventToPingTarget,
  useMinimapPings,
  worldToAxial,
} from './ui/minimapPings.js';
import { useMinimapExtras } from './ui/minimapSupport.js';
import { useDeployGarrison } from './useDeployGarrison.js';
import { useDerivedMatchState } from './useDerivedMatchState.js';
import { useGameActions } from './useGameActions.js';
import { useKeyboardShortcuts } from './useKeyboardShortcuts.js';
import { useRecentCombat } from './useRecentCombat.js';

const LOCAL_PLAYER_INDEX = 0;

export function App() {
  useApplyTheme();
  // One session fetch feeds the layout store's per-player key (one-shot
  // stored-layout apply; guest until the session resolves).
  usePanelLayoutSession();
  // A `?watch=<matchId>` link opens the read-only viewer straight away.
  const [watchId, setWatchId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : watchFromSearch(window.location.search),
  );
  const [gameMode, setGameMode] = useState<GameMode>(() =>
    typeof window !== 'undefined' && watchFromSearch(window.location.search) ? 'spectate' : 'menu',
  );
  const [localPlayerName, setLocalPlayerName] = useState(
    () => localStorage.getItem('aod_player_name') ?? '',
  );
  const [mpMatchInfo, setMpMatchInfo] = useState<MatchInfo | null>(null);

  const mpLobby = useMpLobby(undefined, shouldConnectMpLobby(gameMode));

  useEffect(() => {
    return mpLobby.onMatchFound((info) => {
      setMpMatchInfo(info);
      setGameMode('multiplayer-match');
    });
  }, [mpLobby]);

  // The setup chosen on the New game screen. Each Start makes a new object,
  // which reboots the sim with its ruleset + seats (useSimWorld deps).
  const [matchSetup, setMatchSetup] = useState<MatchSetup | null>(null);
  /** The preset's pause-on-danger for the current match (see handleStartMatch). */
  const [pauseOverride, setPauseOverride] = useState<{
    value: PauseOnDanger;
    settingAtStart: PauseOnDanger;
  } | null>(null);
  const [lastSetup, setLastSetup] = useState<SetupDraft | null>(null);
  const sim = useSimWorld({
    matchSeed: 42,
    localPlayerIndex: LOCAL_PLAYER_INDEX,
    ruleset: matchSetup?.ruleset,
    seats: matchSetup?.seats,
    mapJson: matchSetup?.map,
  });
  const humanAssists = humanAssistsOf(matchSetup);
  const speedCap = humanAssists?.speedCap ?? 4;
  const simSetSpeed = sim.setSpeed;
  const setSpeedCapped = useCallback(
    (multiplier: number) => simSetSpeed(clampSpeed(multiplier, speedCap)),
    [simSetSpeed, speedCap],
  );
  const [showDebug, setShowDebugRaw] = useState(() =>
    readPersistentFlag('aod:showDebug', import.meta.env.DEV),
  );
  const setShowDebug = useCallback((next: boolean | ((v: boolean) => boolean)) => {
    setShowDebugRaw((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      writePersistentFlag('aod:showDebug', value);
      return value;
    });
  }, []);
  const [hoveredTile, setHoveredTile] = useState<SceneNode | null>(null);
  const [hoveredUnit, setHoveredUnit] = useState<SceneNode | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  // Camera go-to requests for the Scene (one glide each, keyed on seq).
  const [cameraRequest, setCameraRequest] = useState<CameraFocusRequest | null>(null);
  const cameraSeq = useRef(0);
  const flyCameraTo = useCallback((position: Vec3, when: CameraFocusRequest['when'] = 'always') => {
    cameraSeq.current += 1;
    // Callers pass board-space coords (snapshot transforms / axialToWorld);
    // the rendered board is scaled (Large Board setting). boardPosition keeps
    // the raw point for consumers that compare against snapshot transforms.
    setCameraRequest({
      position: scalePoint(position),
      boardPosition: position,
      seq: cameraSeq.current,
      when,
    });
  }, []);
  // Where the player explicitly sent the camera (minimap / "Go to"): the
  // audio bed samples the biome there. Selection glides don't count.
  const cameraTarget = cameraRequest?.when === 'always' ? cameraRequest.boardPosition : null;
  const [showPostGameSummary, setShowPostGameSummary] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [coachmarkMenuOpen, setCoachmarkMenuOpen] = useState(false);
  const [combatModalOpen, setCombatModalOpen] = useState(true);
  // Below md the side rails are drawers; at most one is open at a time.
  const [mobilePanel, setMobilePanel] = useState<MobileHudPanel>('none');
  const [combatSelectedIndex, setCombatSelectedIndex] = useState(0);

  // Single coachmark controller instance shared with CoachmarkHost, the
  // HelpButton mounted inside TopBar, and the sequence driver. Per the
  // integration requirement, this is the only useCoachmarks() call in the
  // tree — child components take the controller via props.
  const coachmarkController = useCoachmarks();
  // `startScreenVisible` drives the menu-side `start-game` coachmark. While
  // the menu is mounted, the sequence adapter treats the `match.started`
  // trigger as active so the start-game hint can open against the real
  // MenuScreen Start Game button. Once the player clicks Start, gameMode
  // flips away from 'menu' and the flag becomes false.
  const startScreenVisible = gameMode === 'menu';
  useMatchCoachmarkSequence({
    sim,
    controller: coachmarkController,
    startScreenVisible,
    autoOpen: hintPolicy(humanAssists?.hints).autoOpen,
  });

  // Start a single-player match from the setup screen (Start or Quick start):
  // remember the setup, apply the assists the client handles itself, and
  // hand the ruleset + seats to the sim.
  const resetCoachmarks = coachmarkController.resetAll;
  const startFromSetup = useCallback(
    (draft: SetupDraft) => {
      saveLastSetup(draft);
      const setup = toMatchSetup(draft);
      applyAssistsAtMatchStart(draft.humanAssists, {
        // For this match only: the player's own Settings choice is kept,
        // and wins again as soon as they change it mid-match.
        setPauseOnDanger: (value) =>
          setPauseOverride({ value, settingAtStart: useSettings.getState().pauseOnDanger }),
        resetCoachmarks,
      });
      setMatchSetup(setup);
      setGameMode('single');
    },
    [resetCoachmarks],
  );

  const combatOverlayMode = useSettings((s) => s.combatOverlayMode);

  const pendingOffer = usePendingMerchantOffer(sim.snapshot, LOCAL_PLAYER_INDEX);
  const { diplomacy, dynasty } = useDiplomacyViews(sim.snapshot);
  const espionage = useEspionageView(sim.snapshot, LOCAL_PLAYER_INDEX);
  const heroCaptivity = useHeroCaptivityView(sim.snapshot);
  const heroQuests = useHeroQuestsView(sim.snapshot, LOCAL_PLAYER_INDEX);
  const treatyDesk = useTreatyNews(
    diplomacy,
    dynasty,
    Number(sim.tick),
    LOCAL_PLAYER_INDEX,
    sim.treaty,
  );
  const { toasts: attackToasts, dismiss: dismissToast } = useAttackToasts(
    sim.snapshot,
    LOCAL_PLAYER_INDEX,
  );
  const { toasts: researchToasts, dismiss: dismissResearchToast } = useResearchToasts(
    sim.snapshot,
    LOCAL_PLAYER_INDEX,
  );
  useSounds(sim.events ?? []);

  // Auto-pause when the player looks away or their cities are hit
  // (single-player only; pacing spec §4). Multiplayer renders its own view.
  // Autosave the single-player match (Settings → Gameplay → Autosave), and
  // offer the newest autosave as "Continue" on the menu.
  const autosaveEnabled = useSettings((s) => s.autosaveEnabled);
  useAutosave({
    enabled:
      autosaveEnabled && gameMode === 'single' && sim.status === 'ready' && sim.victory === null,
    tick: Number(sim.tick ?? 0),
    saveGame: sim.saveGame,
  });
  const [continueSave, setContinueSave] = useState<AutosaveEntry | null>(null);
  const [pendingLoad, setPendingLoad] = useState<string | null>(null);
  useEffect(() => {
    if (gameMode !== 'menu') return;
    let live = true;
    void latestAutosave().then((s) => {
      if (live) setContinueSave(s);
    });
    return () => {
      live = false;
    };
  }, [gameMode]);
  useEffect(() => {
    if (!pendingLoad || sim.status !== 'ready') return;
    try {
      sim.loadGame(pendingLoad);
    } catch (err) {
      console.error('Continue: loading the autosave failed', err);
    }
    setPendingLoad(null);
  }, [pendingLoad, sim.status, sim.loadGame]);

  const autoPauseWhenAway = useSettings((s) => s.autoPauseWhenAway);
  const pauseSetting = useSettings((s) => s.pauseOnDanger);
  const pauseOnDanger =
    pauseOverride && pauseSetting === pauseOverride.settingAtStart
      ? pauseOverride.value
      : pauseSetting;
  const pauseController = usePauseController({
    enabled: gameMode === 'single' && sim.victory === null,
    runState: sim.runState,
    pause: sim.pause,
    resume: sim.resume,
    snapshot: sim.snapshot,
    tick: Number(sim.tick ?? 0),
    localPlayer: LOCAL_PLAYER_INDEX,
    autoPauseWhenAway,
    pauseOnDanger,
  });

  // Milestone cinematics (pacing spec §6 C2): `milestone:reached` events play
  // a short, skippable moment. Single-player pauses through the controller's
  // 'cinematic' claim, so a game the player paused stays paused. The seat's
  // ui_mode (kid layer) isn't exposed by the match setup on main yet, so it
  // defaults to 'standard'; window.__aod.testMilestone(p, { uiMode }) can
  // override it.
  const cinematics = useCinematicDirector({
    mode: 'single',
    enabled: gameMode === 'single',
    events: sim.events ?? [],
    tick: Number(sim.tick ?? 0),
    snapshot: sim.snapshot,
    localPlayer: LOCAL_PLAYER_INDEX,
    // Kid-friendly banners (icon + friendly line) for the icons UI mode.
    uiMode: humanAssists?.uiMode,
    pause: {
      pause: pauseController.cinematicPause,
      resume: pauseController.cinematicResume,
    },
  });
  // The finale hands over to VictoryBanner / PostVictorySummary when it ends.
  const finalePlaying =
    cinematics.state.active?.recipeId === 'victory.achieved' ||
    cinematics.state.active?.recipeId === 'victory.lost';

  // R8 graduation: record a local ladder win, and — once a graduated
  // match has booted — drip one of its unannounced modules as a
  // "new rule" cinematic (the count lives in localStorage, so each
  // match of a fresh preset announces exactly one more).
  const t = useT();
  const announcedFor = useRef<MatchSetup | null>(null);
  useEffect(() => {
    const victory = sim.victory;
    const preset = matchSetup?.ruleset.preset;
    if (victory && victory.winner === LOCAL_PLAYER_INDEX && preset && isLadderId(preset)) {
      markMastered(preset);
    }
  }, [sim.victory, matchSetup]);
  useEffect(() => {
    if (sim.status !== 'ready' || !matchSetup) {
      announcedFor.current = null;
      return;
    }
    if (announcedFor.current === matchSetup) return;
    announcedFor.current = matchSetup;
    const preset = matchSetup.ruleset.preset;
    const plan = newRulePlan(preset);
    if (!plan) return;
    const tick = Number(sim.tick ?? 0);
    const result = cinematics.director.offer(
      {
        milestoneId: 'graduation.new_rule',
        playerIndex: LOCAL_PLAYER_INDEX,
        tier: 'big',
        tick,
        data: { name: moduleLabel(t, plan.module) },
      },
      tick,
    );
    // A setting-filtered or not-for-me moment was not shown — don't burn
    // the module's place in the drip.
    if (result !== 'filtered' && result !== 'ignored') {
      markAnnounced(preset, plan.announced + 1);
    }
  }, [sim.status, matchSetup, sim.tick, cinematics.director, t]);

  // Combat visualisation: engagement state (3D jitter + step-back) and
  // the drawer ticker / modal summaries.
  const simEvents = sim.events ?? [];
  const simTick = Number(sim.tick ?? 0);
  const localCapital = useMemo(() => {
    if (!sim.snapshot) return null;
    const globals = sim.snapshot.state.globals as
      | { players?: ReadonlyArray<{ capitalCoord?: { q?: unknown; r?: unknown } | null }> }
      | undefined;
    const coord = globals?.players?.[LOCAL_PLAYER_INDEX]?.capitalCoord;
    if (!coord || typeof coord.q !== 'number' || typeof coord.r !== 'number') {
      return null;
    }
    return { q: coord.q, r: coord.r };
  }, [sim.snapshot]);

  const engagements = useCombatEngagements(simEvents, simTick);
  const drawerSummary = useMemo(
    () => summarizeCombatDrawer(simEvents, LOCAL_PLAYER_INDEX, localCapital),
    [simEvents, localCapital],
  );
  const combatFights = useMemo(() => getCombatFights(simEvents), [simEvents]);
  const combatRoundsByFight = useMemo(() => {
    const map = new Map<string, ReadonlyArray<ReturnType<typeof getCombatRounds>[number]>>();
    for (const f of combatFights) {
      const key = `${f.attackerId}::${f.defenderId}`;
      map.set(key, getCombatRounds(simEvents, f.attackerId, f.defenderId));
    }
    return map;
  }, [combatFights, simEvents]);

  // Recent-combat flag for the music director. Latches true for 5s after any
  // unit:combat / city:damaged / city:captured event, then falls back to peace.
  // Note: because this is a ref, the music director re-derives only on the next
  // sim tick (which re-renders App) — there is up to one tick of lag before the
  // combat -> peace transition. Acceptable per the plan.
  const recentCombatRef = useRecentCombat(sim.events);

  // Sample the biome under the camera focus so the ambient bed matches what
  // the player is actually looking at. Returns null when the camera hasn't
  // been explicitly aimed anywhere.
  const cameraFocusBiome = useMemo(() => {
    if (!sim.snapshot || !cameraTarget) return null;
    return sampleBiomeAtWorld(sim.snapshot, cameraTarget);
  }, [sim.snapshot, cameraTarget]);

  useAudioDirector({
    musicMode:
      gameMode === 'menu' || gameMode === 'setup'
        ? 'menu'
        : sim.victory === null
          ? recentCombatRef.current
            ? 'combat'
            : 'single'
          : sim.victory.winner === LOCAL_PLAYER_INDEX
            ? 'victory'
            : 'defeat',
    recentCombat: recentCombatRef.current,
    doctrine: sim.localPlayer?.dominant ?? null,
    cameraBiome: cameraFocusBiome,
    cameraOverCity: sim.snapshot
      ? isCameraOverCity(sim.snapshot, cameraTarget ?? [0, 0, 0])
      : false,
  });

  // Preload match-critical SFX so first-play is hitch-free. Fires on entry into
  // single or multiplayer-match mode.
  useEffect(() => {
    if (gameMode !== 'single' && gameMode !== 'multiplayer-match') return;
    void preloadCriticalSfx(getMatchCriticalSoundIds());
  }, [gameMode]);

  // ` (backtick) toggles debug HUD; Esc clears selection.
  useKeyboardShortcuts({
    onToggleDebug: () => setShowDebug((v) => !v),
    onClearSelection: () => {
      setSelection(null);
      setCameraRequest(null);
    },
  });

  const handleDeployGarrison = useDeployGarrison(sim, LOCAL_PLAYER_INDEX);

  // Spec §8 suggest mode: while a city is selected, poll the sim's top
  // build/train ideas for the local seat. Applying one routes through the
  // normal start_building intent (the wasm infers units from the id) and
  // re-pulls so the remaining buttons reflect the new queue.
  const { ideas: suggestions, refresh: refreshSuggestions } = useCitySuggestions({
    enabled: humanAssists?.autoManageCities === 'suggest' && selection?.kind === 'city',
    getIdeas: () => sim.suggestIntents(),
  });
  const handleApplySuggestion = useCallback(
    (idea: CitySuggestion) => {
      const action = suggestionAction(idea);
      if (!action) return;
      if (sim.startBuilding(action.cityId, action.buildingId)) {
        refreshSuggestions();
      }
    },
    [sim, refreshSuggestions],
  );

  const handleMoveClick = useCallback(
    (q: number, r: number) => {
      if (tryChampionCast({ snapshot: sim.snapshot, activate: sim.activateLegendAbility }, q, r)) {
        return;
      }
      if (selection?.kind === 'unit') {
        // Ships plot a course over water; everything else steps one hex.
        const node = sim.snapshot?.scene?.children?.find(
          (n) => n.id === selection.id && n.type === 'unit',
        );
        const kind = node?.props?.kind as string | undefined;
        if (kind && NAVAL_KINDS.has(kind)) sim.sailShip(selection.id, q, r);
        else sim.moveUnit(selection.id, q, r);
      }
    },
    [selection, sim],
  );

  const handleMinimapClick = useCallback(
    (q: number, r: number) => {
      const [wx, , wz] = axialToWorld(q, r);
      flyCameraTo([wx, 0, wz]);
    },
    [flyCameraTo],
  );

  // Primary drag on the minimap moves the camera (grab: the ground point under the
  // cursor stays under it). Points arrive in the minimap's unit space, which is the
  // board's world (x, z) — HEX_SIZE is 1 and the axes match axialToWorld.
  const handleMinimapCameraDrag = useCallback((from: MinimapPoint, to: MinimapPoint) => {
    beginCameraGrab([from.x, from.y]);
    dragCameraTo([to.x, to.y]);
  }, []);

  // The drag is over: drop the grab so the next drag starts a fresh one.
  const handleMinimapCameraDragEnd = useCallback(() => {
    cancelCameraGrab();
  }, []);

  // The 3D camera's ground footprint, published from inside the Canvas at ~15 Hz
  // (change-only) in board coordinates — the minimap's unit space.
  const cameraPolygon = useCameraView((st) => st.polygon);
  const cameraPolygonPoints = useMemo(
    () => cameraPolygon?.map(([x, z]) => ({ x, y: z })) ?? null,
    [cameraPolygon],
  );
  // The ground point the camera looks at (the orbit target). The live region
  // announces this, not the footprint's centre: a tilted camera's footprint is
  // a long wedge, so its centroid is nowhere near the camera's real position.
  const cameraFocus = useCameraView((st) => st.focus);

  // Minimap pings (M3): rings on visible tiles where events happen, plus a
  // direct push from event-log clicks. particleEffects off = static rings
  // (prefers-reduced-motion is handled inside the minimap canvas).
  const particleEffects = useSettings((s) => s.particleEffects);
  const minimapViewBox = useSettings((s) => s.minimapViewBox);
  const setMinimapViewBox = useSettings((s) => s.setMinimapViewBox);
  const { pings, pushPing } = useMinimapPings(
    sim.events,
    sim.snapshot,
    sim.minimapSnapshot,
    LOCAL_PLAYER_INDEX,
  );

  // Event-log click: fly to the event's position and ping it there too.
  // The log is the local player's news: other players' small milestones stay out.
  const logEvents = useMemo(
    () => sim.events.filter((e) => visibleInLog(e, LOCAL_PLAYER_INDEX)),
    [sim.events],
  );
  const handleEventClick = useCallback(
    (event: { id: number; type: string; payload?: Record<string, unknown> }) => {
      const target = eventToPingTarget(event, cityPositions(sim.snapshot));
      if (!target) return;
      const [wx, , wz] = axialToWorld(target.q, target.r);
      flyCameraTo([wx, 0, wz]);
      pushPing(target, 'info');
    },
    [sim.snapshot, flyCameraTo, pushPing],
  );

  const {
    handleSetPlayerName,
    handleTileClick,
    handleCityClick,
    handleUnitClick,
    handleBackgroundClick,
    closeSelection,
    startNewMatch,
    handleSave,
    handleLoad,
    handleStartBuilding,
    handleSetCityDoctrine,
  } = useGameActions({ sim, selection, setSelection, setLocalPlayerName });

  // Derived match state — selected node, unit list, building catalog, player
  // color map, and garrison count at the selected city.
  const { selected, unitNodes, buildings, playerColors, garrisonCount } = useDerivedMatchState({
    snapshot: sim.snapshot,
    selection,
    playerHudEntries: sim.playerHudEntries,
  });

  // Champion star on the minimap (empty unless legends.enabled is on).
  const championMarkers = useChampionMarkers(sim.snapshot, LOCAL_PLAYER_INDEX, playerColors);
  const minimapExtras = useMinimapExtras(sim.snapshot, LOCAL_PLAYER_INDEX);
  // The minimap's dock size (width) rides on this wrapper's inline style.
  const minimapDockStyle = usePanelDockStyle('minimap', true);

  // Selecting a tile, city or unit glides the camera to it only when it is
  // off screen or near the edge (e.g. picked from the unit tray, or clicked
  // at the rim of the view). Clicking something already in plain view leaves
  // the camera alone: re-centring on every click felt jumpy, and following a
  // moving unit would fight the player. Keyed on the selection itself, so
  // re-renders and the selected unit moving never re-fly.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const selectionKey = selection ? `${selection.kind}:${selection.id}` : null;
  useEffect(() => {
    const node = selectedRef.current;
    if (!selectionKey || !node) return;
    const [x, y, z] = node.transform.position;
    flyCameraTo([x, y, z], 'if-off-screen');
  }, [selectionKey, flyCameraTo]);

  // The single-player sim boots with the app and would keep ticking behind an
  // online match: a second full world per browser, and its test bridge would
  // shadow the match's. Pause it, and let the match own `window.__aodTest`.
  const inOnlineMatch = gameMode === 'multiplayer-match';
  const pauseSinglePlayerSim = sim.pause;
  useEffect(() => {
    if (inOnlineMatch && sim.status === 'ready') pauseSinglePlayerSim();
  }, [inOnlineMatch, sim.status, pauseSinglePlayerSim]);
  useTestBridge({
    sim,
    localPlayerIndex: LOCAL_PLAYER_INDEX,
    selection,
    setSelection,
    attackToasts,
    enabled: !inOnlineMatch,
  });

  if (gameMode === 'menu') {
    return (
      <>
        <MenuScreen
          continueSave={continueSave}
          onContinue={() => {
            if (!continueSave) return;
            setPendingLoad(continueSave.json);
            setGameMode('single');
          }}
          onStartSingle={() => {
            setLastSetup(loadLastSetup());
            setGameMode('setup');
          }}
          onStartMultiplayer={() => setGameMode('multiplayer-lobby')}
        />
        {/* Same shared controller as the match branch so the start-game
            coachmark can open against the menu's Start Game button while
            the menu is mounted. */}
        <CoachmarkHost controller={coachmarkController} />
      </>
    );
  }

  // ── New game setup ──
  if (gameMode === 'setup') {
    return (
      <SetupScreen
        initialDraft={lastSetup}
        onStart={startFromSetup}
        onBack={() => setGameMode('menu')}
      />
    );
  }

  // ── Spectator (read-only) ──
  if (gameMode === 'spectate' && watchId) {
    return (
      <SpectatorView
        matchId={watchId}
        onBack={() => {
          setWatchId(null);
          setGameMode('menu');
          window.history.replaceState(null, '', window.location.pathname);
        }}
      />
    );
  }

  // ── Multiplayer lobby ──
  if (gameMode === 'multiplayer-lobby') {
    return (
      <LobbyScreen
        mp={mpLobby}
        onWatch={(id) => {
          setWatchId(id);
          setGameMode('spectate');
        }}
        onBack={() => setGameMode('menu')}
        localPlayerName={localPlayerName}
        onSetPlayerName={handleSetPlayerName}
      />
    );
  }

  // ── Multiplayer match ──
  if (gameMode === 'multiplayer-match' && mpMatchInfo) {
    return (
      <MultiplayerMatchView
        matchInfo={mpMatchInfo}
        onBackToMenu={() => {
          setMpMatchInfo(null);
          setGameMode('menu');
        }}
        onRematch={() => {
          mpLobby.rematch();
          setMpMatchInfo(null);
          setGameMode('multiplayer-lobby');
        }}
      />
    );
  }

  if (sim.status === 'loading') return <Splash text="Loading sim core…" />;
  if (sim.status === 'error') return <Splash text={`Failed to load sim: ${sim.error}`} error />;
  if (sim.snapshot === null) return <Splash text="Loading sim core…" />;

  return (
    <ErrorBoundary>
      <div className="relative h-full w-full">
        <TopBar
          food={sim.localTreasury.food}
          industry={sim.localTreasury.industry}
          knowledge={sim.localTreasury.knowledge}
          faith={sim.localTreasury.faith}
          treasury={sim.localTreasury.gold}
          tick={sim.tick}
          runState={sim.runState}
          speed={sim.speed}
          researchState={sim.researchState}
          faithCrisis={sim.faithCrisis}
          startResearch={sim.startResearch}
          pilgrimage={sim.pilgrimage}
          reform={sim.reform}
          pause={sim.pause}
          resume={sim.resume}
          setSpeed={setSpeedCapped}
          maxSpeed={speedCap}
          onNewMatch={startNewMatch}
          onSave={handleSave}
          onLoad={handleLoad}
          onReturnToMenu={() => setGameMode('menu')}
          onOpenSettings={() => setSettingsOpen(true)}
          coachmarkController={coachmarkController}
          coachmarkOpen={coachmarkMenuOpen}
          onCoachmarkOpenChange={setCoachmarkMenuOpen}
          onOpenReferenceHelp={() => setHelpOpen(true)}
        />
        <main aria-label="Game map" className="absolute inset-0">
          <Scene
            snapshot={sim.snapshot}
            events={sim.events ?? []}
            climate={sim.climate}
            sea={sim.sea}
            onTileHover={setHoveredTile}
            onUnitHover={setHoveredUnit}
            onTileClick={handleTileClick}
            onCityClick={handleCityClick}
            onUnitClick={handleUnitClick}
            onMoveClick={handleMoveClick}
            onBuildBridge={(unitId, q, r) => {
              sim.buildBridge(unitId, q, r);
            }}
            onBackgroundClick={() => {
              setCameraRequest(null);
              handleBackgroundClick();
            }}
            selectedTileId={selection?.kind === 'tile' ? selection.id : null}
            selectedCityId={selection?.kind === 'city' ? selection.id : null}
            selectedUnitId={selection?.kind === 'unit' ? selection.id : null}
            focusRequest={cameraRequest}
            doctrine={sim.localPlayer?.dominant ?? null}
            engagements={combatOverlayMode === 'off' ? undefined : engagements}
            currentTick={simTick}
          />
        </main>
        <DoctrinePanel
          state={sim.localPlayer}
          currentTick={sim.tick}
          onInvest={sim.invest}
          mobileOpen={mobilePanel === 'doctrine'}
        />
        {/*
          RightPanel is the persistent right-rail panel. It always mounts so the
          Overview + Diplomacy tabs are reachable without first clicking a tile;
          the Selection tab auto-appears inside the same shell when `selected`
          becomes truthy. RightPanel itself handles the null/selected branching.
        */}
        <ChampionProvider
          snapshot={sim.snapshot}
          tick={sim.tick}
          localPlayerIndex={LOCAL_PLAYER_INDEX}
          summon={sim.summonLegend}
          activate={sim.activateLegendAbility}
        >
          <RightPanel
            selected={selected}
            snapshot={sim.snapshot}
            localPlayerIndex={LOCAL_PLAYER_INDEX}
            matchOver={sim.victory !== null}
            garrisonCount={garrisonCount}
            buildings={buildings}
            treasuryIndustry={sim.localTreasury.industry}
            onAttack={sim.attackCity}
            onDeploy={handleDeployGarrison}
            onStartBuilding={handleStartBuilding}
            onAcceptMerchant={sim.acceptMerchant}
            onDeclineMerchant={sim.declineMerchant}
            dynasty={dynasty}
            espionage={espionage}
            heroCaptivity={heroCaptivity}
            heroQuests={heroQuests}
            diplomacy={diplomacy}
            onProposeTreaty={sim.treaty.propose}
            onDeclareWar={sim.treaty.declareWar}
            onAnswerTreaty={(from, accept) => treatyDesk.answer(from, accept)}
            onProposeMarriage={sim.dynasty.propose}
            onDispatchSpy={sim.espionage.dispatchSpy}
            opsActions={sim.espionage}
            onChooseCapture={sim.espionage.chooseCapture}
            onSpyRescue={sim.espionage.spyRescue}
            onRaidRescue={sim.espionage.raidRescue}
            onPayRansom={sim.espionage.payRansom}
            onChooseHeroCaptivity={sim.heroCaptivity.chooseHeroCaptivity}
            onPayHeroRansom={sim.heroCaptivity.payHeroRansom}
            onDispatchHeroQuest={sim.heroQuests.dispatchHeroQuest}
            onSetDoctrine={handleSetCityDoctrine}
            suggestions={suggestions}
            onApplySuggestion={handleApplySuggestion}
            onActivateAbility={sim.activateHeroAbility}
            onSelectAbility={sim.selectHeroAbility}
            onSetFormation={sim.setFormation}
            onClose={closeSelection}
            tick={sim.tick}
            events={sim.events ?? []}
            // TODO(right-panel-victory-projection): sim exposes VictoryProgressState
            // (tick-milestone shape); RightPanel wants VictoryProgress
            // (per-player wonder/domination/faith %). No projection exists yet —
            // OverviewTab renders its empty state until one is added.
            victoryProgress={null}
            localPlayer={sim.localPlayer ?? null}
            playerHudEntries={sim.playerHudEntries}
            playerColors={playerColors}
            mobileOpen={mobilePanel === 'info'}
          />
        </ChampionProvider>
        {sim.victoryProgress && (
          <VictoryProgressHUD
            victoryProgress={sim.victoryProgress}
            localPlayerIndex={LOCAL_PLAYER_INDEX}
          />
        )}
        {selected && selection?.kind === 'unit' && (
          <UnitSelectionTray
            selected={selected}
            allUnits={unitNodes}
            localPlayerIndex={LOCAL_PLAYER_INDEX}
            onSelectUnit={(id) => setSelection({ kind: 'unit', id })}
          />
        )}
        {sim.victory && !showPostGameSummary && !finalePlaying && (
          <VictoryBanner
            winner={sim.victory.winner}
            path={sim.victory.path}
            localPlayerIndex={LOCAL_PLAYER_INDEX}
            onNewMatch={startNewMatch}
            onReturnToMenu={() => setGameMode('menu')}
            onViewSummary={() => setShowPostGameSummary(true)}
            playedPreset={matchSetup?.ruleset.preset ?? null}
            onTryPreset={(id) => {
              setLastSetup(draftFromPreset(id));
              setGameMode('setup');
            }}
          />
        )}
        {sim.victory && showPostGameSummary && sim.snapshot && !finalePlaying && (
          <PostVictorySummary
            snapshot={sim.snapshot}
            winner={sim.victory.winner}
            path={sim.victory.path}
            localPlayerIndex={LOCAL_PLAYER_INDEX}
            onNewMatch={() => {
              setShowPostGameSummary(false);
              startNewMatch();
            }}
            onReturnToMenu={() => {
              setShowPostGameSummary(false);
              setGameMode('menu');
            }}
          />
        )}
        <AttackToastStack toasts={attackToasts} onDismiss={dismissToast} />
        <ResearchToastStack toasts={researchToasts} onDismiss={dismissResearchToast} />
        <MerchantOfferToast
          cityId={pendingOffer?.cityId ?? ''}
          offer={pendingOffer?.offer ?? null}
          onAccept={sim.acceptMerchant}
          onDecline={sim.declineMerchant}
        />
        <DiplomacyToasts
          snapshot={sim.snapshot}
          players={sim.playerHudEntries}
          localPlayerIndex={LOCAL_PLAYER_INDEX}
          desk={treatyDesk}
          dynasty={sim.dynasty}
        />
        {pauseController.card && (
          <PauseCard
            card={pauseController.card}
            onResume={pauseController.resumeFromCard}
            dominant={sim.localPlayer?.dominant ?? null}
            onGoTo={(position) => {
              // Keep the game paused so the player can look before resuming.
              flyCameraTo(position);
              pauseController.dismissCard();
            }}
          />
        )}
        <TileTooltip
          tile={hoveredTile}
          units={(sim.snapshot?.scene.children ?? []).filter((n) => n.type === 'unit')}
        />
        <UnitTooltip unit={hoveredUnit} />
        <PlayerHud
          players={sim.playerHudEntries}
          className="absolute top-(--hud-strip-top) left-1/2 z-30 w-max max-w-[min(1100px,calc(100vw-1rem))] -translate-x-1/2 shadow-sm"
        />
        <EventLog
          events={logEvents}
          onEventClick={handleEventClick}
          className={mobilePanel === 'none' ? '' : 'max-md:hidden'}
        />
        {sim.minimapSnapshot && (
          <div
            style={minimapDockStyle}
            className={`absolute right-3 bottom-16 z-30 w-[calc(100vw-1.5rem)] md:bottom-4 md:w-(--rail-w) ${
              mobilePanel === 'map'
                ? // Phone: the minimap drawer (spec §6). It sits ABOVE the
                  // mobile HUD toggle bar (which is 44 px tall + padding at
                  // bottom-3), so the two never overlap. The height comes from
                  // the Minimap component itself; here we only place it.
                  'max-md:inset-x-3 max-md:bottom-[4.75rem] max-md:top-auto max-md:w-auto'
                : 'max-md:hidden'
            }`}
          >
            {/* Dock placement wrapper: the minimap fills it; its own collapse
                and toolbar stay as they were. */}
            <Minimap
              snapshot={sim.minimapSnapshot}
              markers={championMarkers}
              {...minimapExtras}
              onTileClick={handleMinimapClick}
              onCameraDrag={handleMinimapCameraDrag}
              onCameraDragEnd={handleMinimapCameraDragEnd}
              cameraPolygon={cameraPolygonPoints}
              showCameraBox={minimapViewBox}
              onToggleCameraBox={() => setMinimapViewBox(!minimapViewBox)}
              cameraFocus={cameraFocus}
              pings={pings}
              animatePings={particleEffects}
              className="w-full"
            />
            <PanelResizeHandle id="minimap" side="left" />
          </div>
        )}
        <DockedDebugHud
          visible={showDebug}
          tick={sim.tick}
          entityCount={sim.tileCount + sim.cityCount}
          determinismHash={sim.determinismHash}
          wsState={gameMode === 'multiplayer-match' ? sim.wsState : undefined}
          getRenderStats={readRenderStats}
          // Top-left of the centre column, clear of both rails. Desktop
          // only — there is no free column on narrow screens.
          positionClassName="fixed top-[var(--hud-panel-top)] right-auto left-[var(--centre-inset)] z-30 hidden opacity-90 lg:block"
        />
        <MobileHudToggles active={mobilePanel} onChange={setMobilePanel} />
        <FooterHint />
        <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
        {combatOverlayMode !== 'off' ? (
          <CombatDrawerTicker
            activeCount={drawerSummary.activeCount}
            nearFights={drawerSummary.nearFights}
            distantCount={drawerSummary.distantCount}
            localCapital={localCapital}
            onClick={() => {
              if (combatOverlayMode === 'modal+drawer') {
                setCombatSelectedIndex(0);
                setCombatModalOpen(true);
              }
            }}
          />
        ) : null}
        {combatOverlayMode === 'modal+drawer' && combatModalOpen ? (
          <CombatModal
            fights={combatFights}
            roundsByFight={combatRoundsByFight}
            selectedIndex={combatSelectedIndex}
            onSelectFight={setCombatSelectedIndex}
            onMinimize={() => setCombatModalOpen(false)}
            onClose={() => setCombatModalOpen(false)}
          />
        ) : null}
        {/* Hints wait while a pause card is up, so they never stack on it. */}
        {!pauseController.card && <CoachmarkHost controller={coachmarkController} />}
        <CinematicOverlay director={cinematics.director} localPlayer={LOCAL_PLAYER_INDEX} />
      </div>
    </ErrorBoundary>
  );
}
